import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Input,
  Modal,
  ModalDialog,
  Sheet,
  Typography,
} from "@mui/joy";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { typeOptions } from "../../constants";
import { Contractor } from "../../entities/contractor";
import { useMyMember } from "../../redux/member/memberHooks";
import { AppConfig } from "../../environement";
import { buildWorkHistoryExcel, ExcelRow, ExportMeta } from "./exportBuilders";
import { ResolvedGig } from "./servicesList";

/**
 * Resolves the deployed resume-PDF service base URL for the active
 * environment, mirroring how `Gig.getInviteURL` derives its host from
 * `AppConfig.api.endpoint`.
 *
 * @returns {string} `https://resume.{env}.rhapsody.la/`
 */
function resumeServiceURL(): string {
  let env = "live";
  if (AppConfig.api.endpoint.includes("beta")) env = "beta";
  if (AppConfig.api.endpoint.includes("demo")) env = "demo";
  return `https://resume.${env}.rhapsody.la/`;
}

/**
 * Triggers a browser download for a blob.
 *
 * @param blob file content
 * @param filename download name
 */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Small uppercase section caption.
 *
 * @param root0
 * @param root0.children
 * @returns {ReactElement} Caption
 */
function Caption({ children }: { children: string }) {
  return (
    <Typography
      level="body3"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontSize: "0.6875rem",
        color: "var(--mui-palette-text-tertiary)",
        px: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Checkbox row used by the organization & type pickers.
 *
 * @param root0
 * @param root0.checked
 * @param root0.indeterminate
 * @param root0.onClick
 * @param root0.children
 * @param root0.bold
 * @returns {ReactElement} CheckRow
 */
function CheckRow({
  checked,
  indeterminate = false,
  onClick,
  children,
  bold = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 0.75,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { background: "rgba(120, 120, 128, 0.08)" },
      }}
    >
      <Checkbox
        size="sm"
        checked={checked}
        indeterminate={indeterminate}
        readOnly
        sx={{ pointerEvents: "none" }}
      />
      <Typography
        level="body2"
        sx={{
          fontWeight: bold ? 600 : 400,
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
        }}
        noWrap
      >
        {children}
      </Typography>
    </Box>
  );
}

/**
 * Export configurator: date range, organizations, session types, and
 * Excel / branded-PDF output. Pro gating happens at the caller.
 *
 * @param root0
 * @param root0.open
 * @param root0.onClose
 * @param root0.items all services available for export
 * @returns {ReactElement} ExportDialog
 */
export function ExportDialog({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: ResolvedGig[];
}) {
  const { member } = useMyMember();
  const [format, setFormat] = useState<"pdf" | "excel">("pdf");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const orgs = useMemo(() => {
    const map = new Map<number, Contractor>();
    items.forEach(({ contractor }) => {
      if (contractor && !map.has(contractor.id))
        map.set(contractor.id, contractor);
    });
    return [...map.values()].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "")
    );
  }, [items]);

  const [orgIDs, setOrgIDs] = useState<Set<number>>(new Set());
  const [typeIDs, setTypeIDs] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) {
      setOrgIDs(new Set(orgs.map((o) => o.id)));
      setTypeIDs(new Set(typeOptions.map((t) => t.sessionTypeID)));
    }
  }, [open, orgs]);

  const allOrgs = orgIDs.size >= orgs.length;
  const allTypes = typeIDs.size >= typeOptions.length;

  const matching = useMemo(() => {
    return items.filter(({ gig, venue, contractor, stage }) => {
      // Only services the musician actually performed: they must be playing,
      // and the job must not sit in a terminus stage (declined, cancelled…)
      if (!gig.isPlaying()) return false;
      if (stage?.terminus) return false;
      if (gig.toBeDetermined()) {
        // Undated services can't appear in a dated history
        if (format === "pdf" || from || to) return false;
      } else {
        const m = gig.from(venue?.tzName);
        if (from && m.isBefore(moment(from, "YYYY-MM-DD").startOf("day")))
          return false;
        if (to && m.isAfter(moment(to, "YYYY-MM-DD").endOf("day")))
          return false;
      }
      if (!allOrgs && !(contractor && orgIDs.has(contractor.id))) return false;
      if (!allTypes && !typeIDs.has(gig.sessionTypeID)) return false;
      return true;
    });
  }, [items, format, from, to, orgIDs, typeIDs, allOrgs, allTypes]);

  const presets: { label: string; from: string; to: string }[] = [
    { label: "All time", from: "", to: "" },
    {
      label: "This year",
      from: moment().startOf("year").format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
    },
    {
      label: "Last 12 months",
      from: moment().subtract(12, "months").format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
    },
    {
      label: "Last 3 years",
      from: moment().subtract(3, "years").format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
    },
  ];

  const exportExcel = async () => {
    const sorted = [...matching].sort((a, b) =>
      a.gig.dateFromUTC.localeCompare(b.gig.dateFromUTC)
    );
    const dated = sorted.filter(({ gig }) => !gig.toBeDetermined());
    const firstDate = dated[0]?.gig.from(dated[0]?.venue?.tzName);
    const lastDate = dated[dated.length - 1]?.gig.from(
      dated[dated.length - 1]?.venue?.tzName
    );
    const periodLabel =
      firstDate && lastDate
        ? firstDate.isSame(lastDate, "month")
          ? firstDate.format("MMMM YYYY")
          : `${firstDate.format("MMM YYYY")} – ${lastDate.format("MMM YYYY")}`
        : "All time";
    const orgCount = new Set(
      sorted.map(({ contractor }) => contractor?.id).filter(Boolean)
    ).size;
    const musicianName =
      [member?.firstName, member?.lastName].filter(Boolean).join(" ") ||
      "Rhapsody Musician";
    const meta: ExportMeta = {
      musicianName,
      email: member?.email,
      periodLabel,
      generatedLabel: moment().format("DD MMM YYYY"),
      organizationsCount: orgCount,
    };

    const rows: ExcelRow[] = sorted.map(
      ({ gig, venue, contractor, project, stage }) => {
        const tz = venue?.tzName;
        const hasTime = !gig.toBeDetermined() && !gig.noTime(tz);
        return {
          date: gig.formattedDate(tz),
          start: hasTime ? gig.startTime(tz) : "",
          end: hasTime ? gig.endTime(tz) : "",
          timezone: hasTime ? gig.from(tz).format("z") : "",
          type: gig.sessionType() || "Service",
          engagement:
            (gig.title ?? "Untitled Service") +
            (project ? ` — ${project.name}` : ""),
          organization: contractor?.name ?? "—",
          venue: venue
            ? `${venue.name} — ${venue.city}, ${venue.state}`
            : gig.venueID === -1
            ? "To be determined"
            : "—",
          status: stage?.wording() ?? "",
          wage: gig.wages || undefined,
        };
      }
    );

    const blob = await buildWorkHistoryExcel(rows, meta);
    saveBlob(blob, "rhapsody-work-history.xlsx");
  };

  /**
   * Builds the raw resume-service payload from the full `items` list plus the
   * user's filter selections and renders the PDF remotely. The service owns all
   * resolution, performed-only/terminus/TBD/date/org/type filtering and mapping
   * — so we ship raw wire objects and the dialog's filter choices, never the
   * locally-filtered `matching` list.
   */
  const exportPdf = async () => {
    const dedupe = <T extends { id: number }>(list: (T | undefined)[]): T[] => {
      const map = new Map<number, T>();
      list.forEach((e) => {
        if (e && !map.has(e.id)) map.set(e.id, e);
      });
      return [...map.values()];
    };

    // The service filters on gig.contractorID, which equals a resolved
    // contractor's sourceRecordID. Map the selected resolved contractor ids
    // (orgIDs) → their sourceRecordIDs.
    const selectedContractors = orgs.filter((o) => orgIDs.has(o.id));

    const payload = {
      gigs: items.map((i) => i.gig),
      venues: dedupe(items.map((i) => i.venue)),
      contractors: dedupe(items.map((i) => i.contractor)),
      projects: dedupe(items.map((i) => i.project)),
      stages: dedupe(items.map((i) => i.stage)),
      member: {
        firstName: member?.firstName,
        lastName: member?.lastName,
        email: member?.email,
      },
      generatedAtUTC: new Date().toISOString(),
      filters: {
        from: from || undefined,
        to: to || undefined,
        organizationIDs: allOrgs
          ? undefined
          : selectedContractors.map((c) => c.sourceRecordID),
        sessionTypeIDs: allTypes ? undefined : [...typeIDs],
      },
    };

    const res = await fetch(resumeServiceURL(), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.error(
        `Resume service failed: ${res.status} ${res.statusText}`,
        await res.text().catch(() => "")
      );
      window.alert(
        "We couldn't generate your work history right now. Please try again."
      );
      return false;
    }
    const blob = await res.blob();
    saveBlob(blob, "rhapsody-work-history.pdf");
    return true;
  };

  const doExport = async () => {
    setBusy(true);
    try {
      if (format === "excel") {
        await exportExcel();
        onClose();
      } else {
        const ok = await exportPdf();
        if (ok) onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const toggle = (
    set: Set<number>,
    setter: (s: Set<number>) => void,
    id: number
  ) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()}>
      <ModalDialog
        layout="center"
        sx={{
          p: 0,
          borderRadius: "20px",
          border: "none",
          width: "min(520px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow:
            "0 12px 24px rgba(0, 0, 0, 0.12), 0 32px 80px rgba(0, 0, 0, 0.28)",
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
          <Typography
            level="h5"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            Export
          </Typography>
          <Typography
            level="body2"
            sx={{ color: "var(--mui-palette-text-secondary)" }}
          >
            Download your services as a spreadsheet, or as a branded work
            history.
          </Typography>
        </Box>

        <Box
          sx={{
            px: 3,
            pb: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            minHeight: 0,
          }}
        >
          <Caption>Format</Caption>
          <Box sx={{ display: "flex", gap: 1.25 }}>
            {[
              {
                key: "pdf" as const,
                icon: "fa-solid fa-file-pdf",
                color: "#FF3B30",
                title: "Work History",
                caption: "Branded PDF document",
              },
              {
                key: "excel" as const,
                icon: "fa-solid fa-file-excel",
                color: "#34C759",
                title: "Excel",
                caption: "Spreadsheet (.xlsx)",
              },
            ].map((f) => (
              <Box
                key={f.key}
                onClick={() => setFormat(f.key)}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  p: 1.25,
                  borderRadius: "12px",
                  cursor: "pointer",
                  border:
                    format === f.key
                      ? "2px solid var(--mui-palette-primary-main)"
                      : "1px solid var(--mui-palette-divider)",
                  background:
                    format === f.key
                      ? "rgba(var(--mui-palette-primary-mainChannel) / 0.06)"
                      : "transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "9px",
                    background: f.color,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  <i className={f.icon}></i>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="body2" sx={{ fontWeight: 600 }}>
                    {f.title}
                  </Typography>
                  <Typography
                    level="body3"
                    sx={{ color: "var(--mui-palette-text-secondary)" }}
                    noWrap
                  >
                    {f.caption}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          {format === "pdf" ? (
            <Typography
              level="body3"
              sx={{ color: "var(--mui-palette-text-secondary)", px: 0.5 }}
              startDecorator={
                <i
                  className="fa-regular fa-circle-info"
                  style={{ fontSize: 11 }}
                ></i>
              }
            >
              A polished, Rhapsody-branded record of your services.
            </Typography>
          ) : (
            []
          )}

          <Box sx={{ mt: 1 }}>
            <Caption>Date range</Caption>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {presets.map((p) => {
              const active = from === p.from && to === p.to;
              return (
                <Typography
                  key={p.label}
                  onClick={() => {
                    setFrom(p.from);
                    setTo(p.to);
                  }}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    px: 1.25,
                    py: 0.375,
                    borderRadius: "999px",
                    cursor: "pointer",
                    userSelect: "none",
                    color: active
                      ? "#fff"
                      : "var(--mui-palette-text-secondary)",
                    background: active
                      ? "var(--mui-palette-primary-main)"
                      : "rgba(120, 120, 128, 0.12)",
                    transition: "all 0.15s ease",
                  }}
                >
                  {p.label}
                </Typography>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", gap: 1.25 }}>
            <Input
              size="sm"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              startDecorator={
                <Typography level="body3" sx={{ fontWeight: 600 }}>
                  From
                </Typography>
              }
              sx={{ flex: 1, borderRadius: "10px" }}
            />
            <Input
              size="sm"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              startDecorator={
                <Typography level="body3" sx={{ fontWeight: 600 }}>
                  To
                </Typography>
              }
              sx={{ flex: 1, borderRadius: "10px" }}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <Caption>Organizations</Caption>
          </Box>
          <Sheet
            variant="soft"
            sx={{ borderRadius: "12px", overflow: "hidden", flexShrink: 0 }}
          >
            <CheckRow
              bold
              checked={allOrgs}
              indeterminate={!allOrgs && orgIDs.size > 0}
              onClick={() =>
                setOrgIDs(allOrgs ? new Set() : new Set(orgs.map((o) => o.id)))
              }
            >
              All Organizations
            </CheckRow>
            <Divider />
            <Box sx={{ maxHeight: 150, overflowY: "auto" }}>
              {orgs.map((o) => (
                <CheckRow
                  key={o.id}
                  checked={orgIDs.has(o.id)}
                  onClick={() => toggle(orgIDs, setOrgIDs, o.id)}
                >
                  {o.name}
                </CheckRow>
              ))}
              {orgs.length === 0 ? (
                <Typography
                  level="body3"
                  sx={{
                    p: 1.5,
                    color: "var(--mui-palette-text-tertiary)",
                  }}
                >
                  No organizations found.
                </Typography>
              ) : (
                []
              )}
            </Box>
          </Sheet>

          <Box sx={{ mt: 1 }}>
            <Caption>Session types</Caption>
          </Box>
          <Sheet
            variant="soft"
            sx={{ borderRadius: "12px", overflow: "hidden", flexShrink: 0 }}
          >
            <CheckRow
              bold
              checked={allTypes}
              indeterminate={!allTypes && typeIDs.size > 0}
              onClick={() =>
                setTypeIDs(
                  allTypes
                    ? new Set()
                    : new Set(typeOptions.map((t) => t.sessionTypeID))
                )
              }
            >
              All Types
            </CheckRow>
            <Divider />
            {typeOptions.map((t) => (
              <CheckRow
                key={t.sessionTypeID}
                checked={typeIDs.has(t.sessionTypeID)}
                onClick={() => toggle(typeIDs, setTypeIDs, t.sessionTypeID)}
              >
                <i className={t.icon} style={{ width: 18, opacity: 0.6 }}></i>
                {t.title}
              </CheckRow>
            ))}
          </Sheet>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 3,
            py: 2,
            borderTop: "1px solid var(--mui-palette-divider)",
            flexShrink: 0,
          }}
        >
          <Typography
            level="body3"
            sx={{ color: "var(--mui-palette-text-secondary)", flex: 1 }}
          >
            {matching.length} performed service
            {matching.length === 1 ? "" : "s"} match your selection — declined
            and cancelled jobs are left out
          </Typography>
          <Button
            variant="plain"
            color="neutral"
            onClick={onClose}
            disabled={busy}
            sx={{ borderRadius: "999px", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            color="info"
            onClick={doExport}
            disabled={matching.length === 0 || busy}
            startDecorator={
              busy ? (
                <CircularProgress size="sm" />
              ) : (
                <i className="fa-solid fa-arrow-down-to-line"></i>
              )
            }
            sx={{ borderRadius: "999px", fontWeight: 600, px: 2.5 }}
          >
            Export
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
